/**
 * AttributeListsPage - Admin UI for managing global attribute lists
 * 
 * Lists can be referenced by option attribute selectors across all product config flows.
 * Supports: Create/Edit/Delete lists, Add/Remove items, CSV import/export
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Search, Trash2, Edit2, Download, Upload, X, Check,
  ChevronRight, List, Tag, FileSpreadsheet, AlertCircle, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Header } from '../../components/Header';
import { toast } from 'sonner';

import { API_BASE } from '../../lib/apiBase';
const API_URL = API_BASE;
const getToken = () => localStorage.getItem('token');

// List Card Component
function ListCard({ list, onSelect, onDelete }) {
  return (
    <div 
      className="bg-card border border-border rounded-xl p-4 hover:border-slate-300 transition-all cursor-pointer group"
      onClick={() => onSelect(list)}
      data-testid={`list-card-${list.slug}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <List className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{list.name}</h3>
            <p className="text-sm text-slate-500">{list.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(list); }}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </div>
      
      {list.description && (
        <p className="text-sm text-slate-600 mt-2 line-clamp-2">{list.description}</p>
      )}
      
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
        <span className="text-sm text-slate-500">
          <Tag className="w-4 h-4 inline mr-1" />
          {list.item_count || 0} items
        </span>
      </div>
    </div>
  );
}

// Create/Edit List Modal
function ListModal({ list, onSave, onClose }) {
  const [name, setName] = useState(list?.name || '');
  const [slug, setSlug] = useState(list?.slug || '');
  const [description, setDescription] = useState(list?.description || '');
  const [saving, setSaving] = useState(false);

  const isEdit = !!list?.id;

  // Auto-generate slug from name
  const handleNameChange = (value) => {
    setName(value);
    if (!isEdit && !slug) {
      setSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;

    setSaving(true);
    try {
      await onSave({ name, slug, description });
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save list');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit List' : 'New Attribute List'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Pantone Colors"
              required
              data-testid="list-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Slug *</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
              placeholder="e.g., pantone-colors"
              required
              disabled={isEdit}
              data-testid="list-slug-input"
            />
            <p className="text-xs text-slate-500 mt-1">Unique identifier (cannot change after creation)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="What is this list used for?"
              rows={2}
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Save Changes' : 'Create List'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// List Detail View (items management)
function ListDetailView({ list, onBack, onUpdate }) {
  const [items, setItems] = useState(list.items || []);
  const [search, setSearch] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importMode, setImportMode] = useState(false);
  const [csvText, setCsvText] = useState('');

  const filteredItems = items.filter(item => 
    item.label.toLowerCase().includes(search.toLowerCase()) ||
    item.value.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddItem = async () => {
    if (!newValue.trim() || !newLabel.trim()) return;
    if (items.some(i => i.value === newValue)) {
      toast.error('Item with this value already exists');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/${list.slug}/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ value: newValue, label: newLabel })
      });

      if (!res.ok) throw new Error('Failed to add item');

      setItems([...items, { value: newValue, label: newLabel }]);
      setNewValue('');
      setNewLabel('');
      toast.success('Item added');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (value) => {
    if (!confirm('Delete this item?')) return;

    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/${list.slug}/items/${encodeURIComponent(value)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!res.ok) throw new Error('Failed to delete item');

      setItems(items.filter(i => i.value !== value));
      toast.success('Item deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleExport = () => {
    const csv = 'value,label\n' + items.map(i => `${i.value},${i.label}`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${list.slug}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (mode = 'append') => {
    const lines = csvText.trim().split('\n');
    const newItems = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || (i === 0 && line.toLowerCase().startsWith('value'))) continue;
      
      const [value, label] = line.split(',').map(s => s.trim());
      if (value && label) {
        newItems.push({ value, label });
      }
    }

    if (newItems.length === 0) {
      toast.error('No valid items found in CSV');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/${list.slug}/import-items?mode=${mode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newItems)
      });

      if (!res.ok) throw new Error('Failed to import items');

      // Refresh the list
      const listRes = await fetch(`${API_URL}/api/product-config/attribute-lists/${list.slug}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const updatedList = await listRes.json();
      setItems(updatedList.items || []);
      
      setImportMode(false);
      setCsvText('');
      toast.success(`Imported ${newItems.length} items (${mode})`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{list.name}</h1>
            <p className="text-sm text-slate-500">{list.slug} · {items.length} items</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportMode(true)}>
            <Upload className="w-4 h-4 mr-1" /> Import
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
        </div>
      </div>

      {/* Import Panel */}
      {importMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-amber-900">Import from CSV</h3>
            <button onClick={() => setImportMode(false)} className="text-amber-600 hover:text-amber-800">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-amber-700 mb-3">
            Paste CSV content below. Format: <code className="bg-amber-100 px-1 rounded">value,label</code>
          </p>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            className="w-full px-3 py-2 border border-amber-300 rounded-lg font-mono text-sm"
            rows={5}
            placeholder="value,label&#10;PMS-100-C,PMS 100 C (Yellow)&#10;PMS-200-C,PMS 200 C (Red)"
          />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleImport('append')} disabled={saving}>
              Append to Existing
            </Button>
            <Button size="sm" onClick={() => handleImport('replace')} disabled={saving}>
              Replace All
            </Button>
          </div>
        </div>
      )}

      {/* Add Item Form */}
      <div className="bg-slate-50 rounded-xl p-4">
        <h3 className="font-medium text-slate-900 mb-3">Add Item</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="Value (e.g., PMS-186-C)"
            data-testid="new-item-value"
          />
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            placeholder="Label (e.g., PMS 186 C (Red))"
            data-testid="new-item-label"
          />
          <Button onClick={handleAddItem} disabled={saving || !newValue || !newLabel}>
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg"
          placeholder="Search items..."
        />
      </div>

      {/* Items List */}
      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Value</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Label</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-slate-600 w-20">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                  {search ? 'No items match your search' : 'No items yet. Add your first item above.'}
                </td>
              </tr>
            ) : (
              filteredItems.map((item, idx) => (
                <tr key={item.value} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-sm text-slate-700">{item.value}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{item.label}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDeleteItem(item.value)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Main Page Component
export default function AttributeListsPage() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [selectedList, setSelectedList] = useState(null);

  const fetchLists = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/attribute-lists`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLists(data);
      }
    } catch (err) {
      console.error('Failed to fetch lists:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const handleCreateList = async (data) => {
    const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to create list');
    }

    toast.success('List created');
    fetchLists();
  };

  const handleUpdateList = async (data) => {
    const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/${editingList.slug}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to update list');
    }

    toast.success('List updated');
    fetchLists();
  };

  const handleDeleteList = async (list) => {
    if (!confirm(`Delete "${list.name}"? This cannot be undone.`)) return;

    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/${list.slug}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!res.ok) throw new Error('Failed to delete list');

      toast.success('List deleted');
      fetchLists();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/attribute-lists/seed-defaults`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!res.ok) throw new Error('Failed to seed lists');

      const data = await res.json();
      toast.success(`Created ${data.created} lists, ${data.skipped} already existed`);
      fetchLists();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredLists = lists.filter(list =>
    list.name.toLowerCase().includes(search.toLowerCase()) ||
    list.slug.toLowerCase().includes(search.toLowerCase())
  );

  // Show detail view if a list is selected
  if (selectedList) {
    return (
      <div className="min-h-screen bg-background" data-testid="attribute-lists-detail-page">
        <Header />
        <main className="p-6 max-w-5xl mx-auto">
          <ListDetailView 
            list={selectedList} 
            onBack={() => { setSelectedList(null); fetchLists(); }}
            onUpdate={() => fetchLists()}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="attribute-lists-page">
      <Header />
      <main className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attribute Lists</h1>
          <p className="text-sm text-slate-500 mt-1">
            Global library of reusable value lists for product configuration
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {lists.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults}>
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Seed Defaults
            </Button>
          )}
          <Button onClick={() => { setEditingList(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" /> New List
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg"
          placeholder="Search lists..."
        />
      </div>

      {/* Lists Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredLists.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl">
          <List className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Attribute Lists</h3>
          <p className="text-slate-500 mb-4">
            {search ? 'No lists match your search' : 'Create your first list or seed the defaults'}
          </p>
          {!search && (
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={handleSeedDefaults}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Seed Defaults
              </Button>
              <Button onClick={() => { setEditingList(null); setShowModal(true); }}>
                <Plus className="w-4 h-4 mr-2" /> New List
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLists.map(list => (
            <ListCard
              key={list.slug}
              list={list}
              onSelect={setSelectedList}
              onDelete={handleDeleteList}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <ListModal
          list={editingList}
          onSave={editingList ? handleUpdateList : handleCreateList}
          onClose={() => { setShowModal(false); setEditingList(null); }}
        />
      )}
      </main>
    </div>
  );
}
